import { motion } from "framer-motion";
import { Eye, ShieldCheck, Target } from "lucide-react";
import { COMPLIANCE, PILLARS } from "@/data";
import { useLang } from "@/i18n";
import { AeroImg } from "@/components/AeroImg";

const EASE = [0.16, 1, 0.3, 1];

export function About() {
  const { t, lang } = useLang();
  return (
    <section id="about" data-testid="about-section" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <span className="font-mono-tag text-xs tracking-[0.3em] uppercase text-blue-400">{t("about_eyebrow")}</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white" data-testid="about-title">{t("about_title")}</h2>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-400">{t("about_lead")}</p>

            <div className="mt-9 space-y-6">
              <div className="flex gap-4 rounded-2xl border border-blue-500/15 bg-[#0B1526] p-5" data-testid="about-vision">
                <div className="shrink-0 rounded-xl bg-blue-600/15 border border-blue-500/25 p-3 text-blue-400 h-fit"><Eye size={20} /></div>
                <div>
                  <div className="font-bold text-white">{t("vision_label")}</div>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{t("vision")}</p>
                </div>
              </div>
              <div className="flex gap-4 rounded-2xl border border-blue-500/15 bg-[#0B1526] p-5" data-testid="about-mission">
                <div className="shrink-0 rounded-xl bg-blue-600/15 border border-blue-500/25 p-3 text-blue-400 h-fit"><Target size={20} /></div>
                <div>
                  <div className="font-bold text-white">{t("mission_label")}</div>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{t("mission")}</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: lang === "ar" ? -40 : 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-3xl border border-blue-500/20 shadow-[0_30px_80px_rgba(2,6,17,0.6)]" data-testid="about-image-frame">
              <AeroImg img="apron" alt="Aerial view of airport apron operations" className="h-[420px] lg:h-[520px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#060B14]/90 via-transparent to-transparent" />
              <div className="absolute bottom-5 inset-x-5 rounded-2xl glass border border-blue-500/20 p-4 flex items-center gap-3">
                <ShieldCheck size={22} className="text-blue-400 shrink-0" />
                <div>
                  <div className="text-sm font-bold text-white">{t("compliance_title")}</div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5" dir="ltr">
                    {COMPLIANCE.map((c) => (
                      <span key={c} className="font-mono-tag text-[10px] tracking-wide text-blue-300 border border-blue-500/30 rounded-full px-2 py-0.5">{c}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-20">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="text-2xl sm:text-3xl font-extrabold text-white mb-8"
            data-testid="about-why-title"
          >
            {t("why_title")}
          </motion.h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="about-pillars">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title_en}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
                className="rounded-2xl border border-blue-500/15 bg-[#0B1526] p-6 hover:border-blue-500/40 transition-colors duration-300"
              >
                <div className="font-mono-tag text-blue-500 text-sm font-semibold" dir="ltr">0{i + 1}</div>
                <div className="mt-3 font-bold text-white">{p[`title_${lang}`]}</div>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{p[`desc_${lang}`]}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
